/**
 * Client-side DRAFT state for the cafeteria Serve surface.
 *
 * The school-serve per-row quantity tally (`quantityServed`) and reported
 * issues are local until the mealtime is COMPLETED (committed server-side).
 * This store persists that uncommitted draft to localStorage so it survives
 * navigating away and back — keyed by (school location × service date ×
 * mealtime). A draft is dropped once every map is empty (e.g. after
 * completion clears them), and drafts older than `SERVE_DRAFT_MAX_AGE_DAYS`
 * are pruned so abandoned ones don't pile up.
 *
 * PROD-1278 — student serves now write to the server the moment "Mark as
 * served" is tapped, so `servedOverrides` and `componentPicks` are LEGACY:
 * still read (a tablet upgrading mid-service carries them) and drained
 * once through `planServeDraftMigration`, never written to again.
 *
 * Manual localStorage sync (no zustand persist middleware), mirroring the
 * chefs temp-log-tasks store.
 */

import { create } from 'zustand'

import type { StudentMealGroup } from '@/lib/cafeteria/format'

const DRAFTS_KEY = 'ordo.cafeteria.serveDrafts'
const SERVE_DRAFT_MAX_AGE_DAYS = 3
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * One reported serve issue, keyed in `issues` by the student card key
 * (group key for ordered kids, roster user id for no-order kids). The
 * affected `orderItemIds` commit through `voidUnassigned` with the
 * worker's typed `reason` at mealtime completion; a no-order kid's
 * issue carries no order items (audit-trail only).
 */
export interface ServeIssueDraft {
  studentUserId: string | null
  studentName: string
  reason: string
  orderItemIds: string[]
}

/**
 * PROD-1121 — one order item's serve-confirm picks, staged with the local
 * served toggle and committed with the mealtime (rides the item's
 * `serveAnonymous` call). `servedMilkComponentId` follows the wire's
 * three-state contract: the KEY IS ABSENT when the milk radio never
 * rendered; null = "No milk"; id = the campus milk SKU taken. JSON
 * round-trips preserve the absent key, so the draft survives reloads
 * without losing the distinction.
 */
export interface ServeItemPicksDraft {
  acceptedComponentIds: string[]
  servedMilkComponentId?: string | null
}

/** One serve session's uncommitted state, plus a last-touched stamp for pruning. */
export interface ServeDraft {
  /** LEGACY (pre-PROD-1278) — student toggles; drained by `planServeDraftMigration`. */
  servedOverrides: Record<string, boolean>
  quantityServed: Record<string, number>
  /** Optional — drafts saved before issues shipped load unchanged. */
  issues?: Record<string, ServeIssueDraft>
  /** Optional (PROD-1121) — serve-confirm picks keyed by orderItemId. */
  componentPicks?: Record<string, ServeItemPicksDraft>
  updatedAt: number
}

/** Stable key for a serve draft: one mealtime of a school location's service day. */
export function serveDraftKey(locationId: string, serviceDate: string, mealtime: string): string {
  return `${locationId}::${serviceDate}::${mealtime}`
}

function read<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') {
    return fallback
  }
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown) {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage unavailable / quota — non-fatal.
  }
}

/** True when a draft holds no uncommitted toggles or issues. Pure + tested. */
export function isServeDraftEmpty(
  draft: Pick<ServeDraft, 'servedOverrides' | 'quantityServed' | 'issues' | 'componentPicks'>,
): boolean {
  return (
    Object.keys(draft.servedOverrides).length === 0 &&
    Object.keys(draft.quantityServed).length === 0 &&
    Object.keys(draft.issues ?? {}).length === 0 &&
    Object.keys(draft.componentPicks ?? {}).length === 0
  )
}

/** Drop drafts last touched before `now - maxAgeDays`. Pure + tested. */
export function pruneServeDrafts(
  drafts: Record<string, ServeDraft>,
  now: number,
  maxAgeDays: number = SERVE_DRAFT_MAX_AGE_DAYS,
): Record<string, ServeDraft> {
  const cutoff = now - maxAgeDays * DAY_MS
  let changed = false
  const next: Record<string, ServeDraft> = {}
  for (const [key, draft] of Object.entries(drafts)) {
    if (draft.updatedAt >= cutoff) {
      next[key] = draft
    } else {
      changed = true
    }
  }
  return changed ? next : drafts
}

interface ServeDraftStore {
  drafts: Record<string, ServeDraft>
  /** Read a session's draft (empty maps when none persisted). */
  getDraft: (
    key: string,
  ) => Required<Pick<ServeDraft, 'servedOverrides' | 'quantityServed' | 'issues' | 'componentPicks'>>
  /** Persist a session's draft. Removes the entry when it holds nothing. */
  saveDraft: (
    key: string,
    draft: Pick<ServeDraft, 'servedOverrides' | 'quantityServed' | 'issues' | 'componentPicks'>,
  ) => void
  /** Drop a session's draft (after the mealtime is committed/completed). */
  clearDraft: (key: string) => void
  /**
   * One-time recovery for the mealtime-key rollout: drafts staged BEFORE
   * serve keys carried a mealtime sit under the legacy day-scoped key
   * (`location::date`), which nothing reads anymore — mid-service toggles
   * looked "undone" after the deploy. The first serve session opened for
   * that (location, date) claims the legacy draft into its own mealtime
   * key; the legacy entry is removed either way, so a second mealtime can
   * never re-adopt it (that replay was the original bug). A draft already
   * under the new key wins — the legacy one is just dropped then.
   * Returns true when a legacy draft was adopted into `mealtime`'s key.
   */
  adoptLegacyDraft: (locationId: string, serviceDate: string, mealtime: string) => boolean
}

export const useCafeteriaServeDraftStore = create<ServeDraftStore>((set, get) => ({
  drafts: read<Record<string, ServeDraft>>(DRAFTS_KEY, {}),
  getDraft: (key) => {
    const draft = get().drafts[key]
    return {
      servedOverrides: draft?.servedOverrides ?? {},
      quantityServed: draft?.quantityServed ?? {},
      // Optional on stored drafts — ones saved before issues shipped.
      issues: draft?.issues ?? {},
      // Optional on stored drafts — ones saved before PROD-1121 shipped.
      componentPicks: draft?.componentPicks ?? {},
    }
  },
  saveDraft: (key, draft) => {
    if (isServeDraftEmpty(draft)) {
      get().clearDraft(key)
      return
    }
    const now = Date.now()
    const pruned = pruneServeDrafts(get().drafts, now)
    const drafts = {
      ...pruned,
      [key]: {
        servedOverrides: draft.servedOverrides,
        quantityServed: draft.quantityServed,
        issues: draft.issues ?? {},
        componentPicks: draft.componentPicks ?? {},
        updatedAt: now,
      },
    }
    write(DRAFTS_KEY, drafts)
    set({ drafts })
  },
  clearDraft: (key) => {
    if (!get().drafts[key]) {
      return
    }
    const drafts = { ...get().drafts }
    delete drafts[key]
    write(DRAFTS_KEY, drafts)
    set({ drafts })
  },
  adoptLegacyDraft: (locationId, serviceDate, mealtime) => {
    const legacyKey = `${locationId}::${serviceDate}`
    const legacy = get().drafts[legacyKey]
    if (!legacy) {
      return false
    }
    const key = serveDraftKey(locationId, serviceDate, mealtime)
    const drafts = { ...get().drafts }
    delete drafts[legacyKey]
    const adopted = !drafts[key]
    if (adopted) {
      drafts[key] = legacy
    }
    write(DRAFTS_KEY, drafts)
    set({ drafts })
    return adopted
  },
}))

// ---------------------------------------------------------------------------
// PROD-1278 — one-shot migration of pre-redesign student toggles
// ---------------------------------------------------------------------------

const MIGRATED_KEY_PREFIX = 'ordo.cafeteria.serveDraftMigrated.v1::'

export interface ServeDraftMigrationServe {
  orderItemId: string
  picks: ServeItemPicksDraft | undefined
}

export interface ServeDraftMigrationPlan {
  /** One serve per item the worker had marked served on this tablet but never committed. */
  serves: ServeDraftMigrationServe[]
  /**
   * Students the worker had explicitly toggled back to NOT served while the
   * server already shows them served — an intent the new model cannot honour
   * (the serve is real). Counted so the toast can say so.
   */
  droppedUnserved: number
}

/**
 * What a tablet that upgrades mid-service still owes the server: every
 * `servedOverrides[key] === true` whose student has items still awaiting a
 * serve becomes one serve per item, carrying the picks the worker staged.
 * Pure — the page fires the serves and then empties the two legacy maps.
 */
export function planServeDraftMigration(
  draft: Pick<ServeDraft, 'servedOverrides' | 'componentPicks'>,
  groups: ReadonlyArray<StudentMealGroup>,
  itemAwaitingServe: (item: StudentMealGroup['items'][number]) => boolean,
): ServeDraftMigrationPlan {
  const byKey = new Map(groups.map((group) => [group.key, group]))
  const serves: ServeDraftMigrationServe[] = []
  let droppedUnserved = 0
  for (const [key, served] of Object.entries(draft.servedOverrides)) {
    const group = byKey.get(key)
    if (!group) {
      continue
    }
    if (!served) {
      if (group.items.some((item) => !itemAwaitingServe(item))) {
        droppedUnserved += 1
      }
      continue
    }
    for (const item of group.items) {
      if (itemAwaitingServe(item)) {
        serves.push({ orderItemId: item.orderItemId, picks: draft.componentPicks?.[item.orderItemId] })
      }
    }
  }
  return { serves, droppedUnserved }
}

/** The slice of Storage the migration marker needs — injectable for tests. */
export interface MigrationMarkerStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function defaultMarkerStorage(): MigrationMarkerStorage | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/**
 * Whether the legacy toggles under `draftKey` were already drained on this
 * browser. No storage at all reads as "migrated" — nothing to drain.
 */
export function readServeDraftMigrated(draftKey: string, storage = defaultMarkerStorage()): boolean {
  if (!storage) {
    return true
  }
  try {
    return storage.getItem(MIGRATED_KEY_PREFIX + draftKey) !== null
  } catch {
    return true
  }
}

const TALLY_MIGRATED_KEY_PREFIX = 'ordo.cafeteria.tallyDraftMigrated.v1::'

/**
 * PROD-1278 Phase 4 — whether the local school tallies under `draftKey` were
 * already posted as deltas on this browser. Same contract as
 * `readServeDraftMigrated`: no storage reads as "migrated".
 */
export function readTallyDraftMigrated(draftKey: string, storage = defaultMarkerStorage()): boolean {
  if (!storage) {
    return true
  }
  try {
    return storage.getItem(TALLY_MIGRATED_KEY_PREFIX + draftKey) !== null
  } catch {
    return true
  }
}

/** Mark the local tallies under `draftKey` drained — written BEFORE the deltas fire. */
export function markTallyDraftMigrated(
  draftKey: string,
  now: number = Date.now(),
  storage = defaultMarkerStorage(),
): void {
  if (!storage) {
    return
  }
  try {
    storage.setItem(TALLY_MIGRATED_KEY_PREFIX + draftKey, String(now))
  } catch {
    // localStorage unavailable / quota — non-fatal.
  }
}

/** Mark the legacy toggles under `draftKey` drained — written BEFORE the serves fire. */
export function markServeDraftMigrated(
  draftKey: string,
  now: number = Date.now(),
  storage = defaultMarkerStorage(),
): void {
  if (!storage) {
    return
  }
  try {
    storage.setItem(MIGRATED_KEY_PREFIX + draftKey, String(now))
  } catch {
    // localStorage unavailable / quota — non-fatal.
  }
}
